import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Checkbox,
  Text,
  HStack,
  Icon,
  Link,
  useColorModeValue,
  Box,
} from '@chakra-ui/react';
import { Briefcase, User, Building2, CheckCircle2, Eye } from 'lucide-react';
import { Issue, ActionItem, User as UserType, Rock } from '../../types';
import {
  calculateDaysOpen,
  formatDate,
  formatDaysOpen,
  getIssueRockName,
  getIssueActionItems,
  getIssueAssignee,
} from '../issues.util';

interface IssuesTableProps {
  issues: Issue[];
  actionItems: ActionItem[];
  users: UserType[];
  rocks: Rock[];
  onResolve?: (issueId: string) => void;
  onView?: (issueId: string) => void;
}

/**
 * Issues Table Component - Compact table layout
 */
export function IssuesTable({
  issues,
  actionItems,
  users,
  rocks,
  onResolve,
  onView,
}: IssuesTableProps) {
  const headerBg = useColorModeValue('gray.50', 'gray.800');
  const headerColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const textColorLight = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  if (issues.length === 0) {
    return (
      <Box py={8} textAlign="center">
        <Text color={textColorLight}>No issues to display</Text>
      </Box>
    );
  }

  return (
    <Box w="100%" overflowX="auto">
      <TableContainer w="100%" minW="800px">
        <Table variant="simple" size="sm" w="100%">
          <Thead bg={headerBg}>
            <Tr>
              <Th px={2} py={2} width="40px" whiteSpace="nowrap">
                <Checkbox size="sm" />
              </Th>
              <Th px={2} py={2} textAlign="left" fontSize="xs" fontWeight="medium" color={headerColor} textTransform="uppercase" letterSpacing="wider" minW="200px">
                ISSUE
              </Th>
              <Th px={2} py={2} textAlign="left" fontSize="xs" fontWeight="medium" color={headerColor} textTransform="uppercase" letterSpacing="wider" minW="150px">
                ROCK / COMPANY
              </Th>
              <Th px={2} py={2} textAlign="left" fontSize="xs" fontWeight="medium" color={headerColor} textTransform="uppercase" letterSpacing="wider" minW="100px">
                ACTION ITEMS
              </Th>
              <Th px={2} py={2} textAlign="left" fontSize="xs" fontWeight="medium" color={headerColor} textTransform="uppercase" letterSpacing="wider" minW="150px">
                ASSIGNED
              </Th>
              <Th px={2} py={2} textAlign="left" fontSize="xs" fontWeight="medium" color={headerColor} textTransform="uppercase" letterSpacing="wider" minW="120px" whiteSpace="nowrap">
                CREATED
              </Th>
              <Th px={2} py={2} textAlign="left" fontSize="xs" fontWeight="medium" color={headerColor} textTransform="uppercase" letterSpacing="wider" minW="100px" whiteSpace="nowrap">
                DAYS OPEN
              </Th>
              <Th px={2} py={2} textAlign="left" fontSize="xs" fontWeight="medium" color={headerColor} textTransform="uppercase" letterSpacing="wider" minW="120px" whiteSpace="nowrap">
                ACTIONS
              </Th>
            </Tr>
          </Thead>
        <Tbody>
          {issues.map((issue) => {
            const issueActionItems = getIssueActionItems(issue.id, actionItems);
            const assignee = getIssueAssignee(issue, users);
            const rockName = getIssueRockName(issue, rocks);
            const daysOpen = calculateDaysOpen(issue.firstOpenedDate);
            const isResolved = issue.status === 'solved';

            return (
              <Tr key={issue.id} _hover={{ bg: hoverBg }}>
                <Td px={2} py={2}>
                  <Checkbox size="sm" />
                </Td>
                <Td px={2} py={2}>
                  <Box maxW="200px">
                    <Text fontWeight="medium" fontSize="sm" color={textColor} isTruncated>
                      {issue.name}
                    </Text>
                    <Text fontSize="xs" color={textColorLight} mt={0.5} noOfLines={2}>
                      {issue.description}
                    </Text>
                  </Box>
                </Td>
                <Td px={2} py={2}>
                  {issue.isCompany ? (
                    <HStack spacing={1}>
                      <Icon as={Briefcase} w={3} h={3} color={textColorLight} />
                      <Text fontSize="sm" color={textColor} isTruncated>
                        Company-wide
                      </Text>
                    </HStack>
                  ) : rockName ? (
                    <HStack spacing={1}>
                      <Icon as={Eye} w={3} h={3} color="blue.500" />
                      <Text fontSize="sm" color={textColor} isTruncated>
                        {rockName}
                      </Text>
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color={textColorLight}>
                      —
                    </Text>
                  )}
                </Td>
                <Td px={2} py={2}>
                  {issueActionItems.length > 0 ? (
                    <HStack spacing={1}>
                      <Icon as={CheckCircle2} w={3} h={3} color="green.500" />
                      <Text fontSize="sm" color={textColor}>
                        {issueActionItems.length}
                      </Text>
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color={textColorLight}>
                      No Action Items
                    </Text>
                  )}
                </Td>
                <Td px={2} py={2}>
                  {assignee ? (
                    <Box maxW="150px">
                      <HStack spacing={1} mb={0.5}>
                        <Icon as={User} w={3} h={3} color={textColorLight} />
                        <Text fontSize="sm" color={textColor} isTruncated>
                          {assignee.name}
                        </Text>
                      </HStack>
                      {assignee.title && (
                        <HStack spacing={1}>
                          <Icon as={Building2} w={3} h={3} color={textColorLight} />
                          <Text fontSize="xs" color={textColorLight} isTruncated>
                            {assignee.title}
                          </Text>
                        </HStack>
                      )}
                    </Box>
                  ) : (
                    <Text fontSize="sm" color={textColorLight}>
                      —
                    </Text>
                  )}
                </Td>
                <Td px={2} py={2} whiteSpace="nowrap">
                  <Text fontSize="sm" color={textColor}>
                    {formatDate(issue.createdAt)}
                  </Text>
                </Td>
                <Td px={2} py={2} whiteSpace="nowrap">
                  <Text fontSize="sm" color={textColor}>
                    {formatDaysOpen(daysOpen)}
                  </Text>
                </Td>
                <Td px={2} py={2} whiteSpace="nowrap">
                  <HStack spacing={3}>
                    <Link
                      fontSize="sm"
                      color="blue.500"
                      onClick={() => onView?.(issue.id)}
                      cursor="pointer"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      View
                    </Link>
                    {!isResolved && onResolve && (
                      <Link
                        fontSize="sm"
                        color="green.500"
                        onClick={() => onResolve(issue.id)}
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        Resolve
                      </Link>
                    )}
                  </HStack>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      </TableContainer>
    </Box>
  );
}
